import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi, tokenStore } from "./api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  // user states: undefined = loading, null = unauthenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    if (!tokenStore.get()) {
      setUser(null);
      return null;
    }
    try {
      const u = await authApi.me();
      setUser(u);
      return u;
    } catch (err) {
      if (err?.response?.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      tokenStore.clear();
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const r = await authApi.login({ email, password });
    tokenStore.set(r.token);
    // Reset session-scoped UI state so stale filters from a previous session
    // don't confuse the user on next login. Safe to swallow errors here —
    // private-browsing storage failures don't impact correctness.
    try { localStorage.removeItem("qfresh_filters"); } catch (e) { void e; }
    setUser(r.user);
    return r.user;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const r = await authApi.register({ email, password, name });
    tokenStore.set(r.token);
    try { localStorage.removeItem("qfresh_filters"); } catch (e) { void e; }
    setUser(r.user);
    return r.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.warn("Logout request failed (clearing local state anyway):", err);
    }
    tokenStore.clear();
    try { localStorage.removeItem("qfresh_filters"); } catch (e) { void e; }
    setUser(null);
  }, []);

  const updateMe = useCallback(async (patch) => {
    const u = await authApi.updateMe(patch);
    setUser(u);
    return u;
  }, []);

  const setHouseholdId = useCallback(
    (id) => setUser((u) => (u ? { ...u, household_id: id } : u)),
    []
  );

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh, updateMe, setHouseholdId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  return v;
};
